import { GoogleGenAI } from "@google/genai";

export const analyzeImage = async (
  apiKey: string,
  base64Image: string,
  modelId: string
): Promise<string> => {
  if (!apiKey) {
    throw new Error('API Key is required');
  }

  const ai = new GoogleGenAI({ apiKey });

  // Extract base64 data and mimeType
  const mimeType = base64Image.substring(base64Image.indexOf(':') + 1, base64Image.indexOf(';')) || 'image/jpeg';
  const base64Data = base64Image.split(',')[1] || base64Image;

  // CRITICAL: Prompt engineering to force length between 1200 and 1400 characters.
  const systemPrompt = `You are a hyper-descriptive AI Prompt Engineer for high-end image generation (Midjourney v6, DALL-E 3).
  
  YOUR GOAL: Analyze the image and write a MASSIVELY DETAILED textual description prompt.
  
  CRITICAL LENGTH CONSTRAINTS:
  1. MINIMUM LENGTH: 1200 characters. (You MUST be extremely verbose).
  2. MAXIMUM LENGTH: 1400 characters. (Do not exceed this).
  
  INSTRUCTIONS TO ACHIEVE LENGTH:
  - Do not summarize. Describe every single pixel, texture, lighting gradient, and background element.
  - Describe the emotions, the atmosphere, the cultural references, the camera lens type, the f-stop, the shutter speed simulation.
  - Use rich, flowery, and technical artistic vocabulary.
  - If you run out of things to describe, elaborate on the texture of surfaces (e.g., the weave of fabric, the pores of skin, the grain of wood).
  - Describe the lighting in excruciating detail (direction, color temperature, volumetric effects, shadow softness).
  - Describe the color palette using specific art terms (e.g., "cerulean blue," "burnt sienna," "neon cyberpunk magenta").
  
  STRUCTURE:
  - Subject details (Appearance, Pose, Clothing, Micro-details)
  - Environment & Background (Depth, Objects, Atmosphere)
  - Artistic Style & Medium (e.g., "Hyper-realistic oil painting by Greg Rutkowski," "8k Unreal Engine 5 render")
  - Technical Camera Settings (Focal length, lighting setup)
  
  Output ONLY the raw prompt text. No introductions.`;

  try {
    const response = await ai.models.generateContent({
      model: modelId,
      contents: {
        parts: [
          {
            inlineData: {
              mimeType: mimeType,
              data: base64Data
            }
          },
          {
            text: systemPrompt
          }
        ]
      },
      config: {
        temperature: 0.7, // Slightly higher creativity to help generate more text
        topK: 40,
        topP: 0.95,
      }
    });

    let text = response.text || "";
    
    // Strict enforcement of 1400 character limit
    if (text.length > 1400) {
      text = text.substring(0, 1400);
      // Attempt to cut off cleanly at the last period if it's within a reasonable range
      const lastPeriod = text.lastIndexOf('.');
      if (lastPeriod > 1200) { 
        text = text.substring(0, lastPeriod + 1);
      }
    } else if (text.length < 100) {
      // Basic error handling for empty/failed responses
      throw new Error("Generated prompt was too short. Please try again or check the image.");
    }
    
    return text;
  } catch (error: any) {
    console.error('Error calling Gemini API:', error);
    throw new Error(error.message || 'Failed to generate prompt with Gemini');
  }
};

export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
};